import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { TrendingUp, Building2, Handshake, CheckCircle2, ArrowRight } from 'lucide-react';

const HowItWorksPage = () => {
  const { t } = useI18n();

  const investorSteps = [
    {
      number: '01',
      title: 'Registro y Verificación',
      description: 'Cree su cuenta, complete su perfil de inversionista y pase por nuestro proceso de verificación KYC/AML.',
    },
    {
      number: '02',
      title: 'Exploración de Oportunidades',
      description: 'Revise las oportunidades disponibles, acceda a documentación detallada y análisis de cada proyecto.',
    },
    {
      number: '03',
      title: 'Due Diligence Personal',
      description: 'Analice la información, consulte con sus asesores y tome una decisión informada.',
    },
    {
      number: '04',
      title: 'Compromiso de Inversión',
      description: 'Seleccione su monto de inversión, firme los documentos requeridos y transfiera los fondos.',
    },
    {
      number: '05',
      title: 'Seguimiento y Reportes',
      description: 'Reciba actualizaciones periódicas sobre el avance del proyecto y rendimiento de su inversión.',
    },
  ];

  const contributorSteps = [
    {
      number: '01',
      title: 'Registro de Propietario',
      description: 'Cree su cuenta como aportador de inmueble y complete su perfil con información de contacto y representación.',
    },
    {
      number: '02',
      title: 'Envío de Inmueble',
      description: 'Registre su propiedad con información básica: ubicación, tipo, superficie, uso actual y valor estimado.',
    },
    {
      number: '03',
      title: 'Documentación',
      description: 'Suba los documentos requeridos: escrituras, identificaciones, comprobantes de propiedad y situación legal.',
    },
    {
      number: '04',
      title: 'Revisión Inicial',
      description: 'Nuestro equipo evalúa la viabilidad del inmueble y solicita información adicional si es necesario.',
    },
    {
      number: '05',
      title: 'Estructuración',
      description: 'Si el inmueble es aprobado, trabajamos en conjunto para definir la estructura de aportación y términos.',
    },
  ];

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <section className="section-spacing border-b border-border">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="caption mb-4">Proceso</p>
            <h1 className="heading-1 mb-6">Cómo Funciona</h1>
            <p className="body-lg">
              Conozca el proceso para invertir en nuestras oportunidades inmobiliarias o aportar 
              su propiedad a nuestra estructura de inversión.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="section-spacing">
        <div className="container-app">
          <Tabs defaultValue="investors" className="w-full">
            <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 mb-16">
              <TabsTrigger value="investors" className="gap-2">
                <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
                Para Inversionistas
              </TabsTrigger>
              <TabsTrigger value="contributors" className="gap-2">
                <Building2 className="h-4 w-4" strokeWidth={1.5} />
                Para Aportadores
              </TabsTrigger>
            </TabsList>

            <TabsContent value="investors">
              <div className="max-w-4xl mx-auto">
                <div className="space-y-12">
                  {investorSteps.map((step, index) => (
                    <div key={index} className="flex gap-8">
                      <div className="flex-shrink-0">
                        <span className="text-5xl font-light text-muted-foreground/30">
                          {step.number}
                        </span>
                      </div>
                      <div className="flex-1 pt-2">
                        <h3 className="heading-3 mb-3">{step.title}</h3>
                        <p className="body-text">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-16 text-center">
                  <Link to="/register">
                    <Button className="btn-primary gap-2">
                      Comenzar a Invertir
                      <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contributors">
              <div className="max-w-4xl mx-auto">
                <div className="space-y-12">
                  {contributorSteps.map((step, index) => (
                    <div key={index} className="flex gap-8">
                      <div className="flex-shrink-0">
                        <span className="text-5xl font-light text-muted-foreground/30">
                          {step.number}
                        </span>
                      </div>
                      <div className="flex-1 pt-2">
                        <h3 className="heading-3 mb-3">{step.title}</h3>
                        <p className="body-text">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-16 text-center">
                  <Link to="/submit-property">
                    <Button className="btn-primary gap-2">
                      Aportar Inmueble
                      <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-card">
        <div className="container-app">
          <div className="text-center mb-16">
            <p className="caption mb-4">Beneficios</p>
            <h2 className="heading-2">¿Por Qué Silver Brick?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-premium text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-4 text-primary" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-3">Transparencia Total</h3>
              <p className="text-sm text-muted-foreground">
                Acceso completo a documentación, reportes periódicos y comunicación directa con el equipo.
              </p>
            </div>
            <div className="card-premium text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-4 text-primary" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-3">Procesos Rigurosos</h3>
              <p className="text-sm text-muted-foreground">
                Due diligence exhaustivo, estructuración legal robusta y gestión profesional de activos.
              </p>
            </div>
            <div className="card-premium text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-4 text-primary" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-3">Alineación de Intereses</h3>
              <p className="text-sm text-muted-foreground">
                Nuestro éxito está directamente ligado al éxito de nuestros inversionistas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="section-spacing">
        <div className="container-app text-center">
          <h2 className="heading-3 mb-6">¿Tiene Más Preguntas?</h2>
          <p className="body-text max-w-2xl mx-auto mb-8">
            Nuestro equipo está disponible para resolver cualquier duda sobre el proceso de inversión o aportación de inmuebles.
          </p>
          <Link to="/contact">
            <Button variant="outline" className="btn-outline">
              Contáctenos
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
